import { useMemo } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Control, useController } from "react-hook-form";

import { SavedBlob } from "api/generated/model";
import { FieldBuilder } from "components/fieldBuilder/fieldBuilder";
import {
    CommonFieldTypeName,
    FieldPlaceholderCmp,
} from "components/fieldBuilder/utils";
import { useBlobsFormAtom } from "org/control/view/components/tasks/Tasks";

import { TestingProgramFormSchema } from "../../useTestingProgramViewFormSchema";

import { ConclusionFieldCmp } from "./ConclusionFieldCmp";
import { ConclusionFieldCustomSection } from "./ConclusionFieldCustomSection";
import { ConclusionFieldDrawerHeader } from "./ConclusionFieldDrawerHeader";
import {
    createNewSampleConclusionField,
    getConclusionFieldTypeConditionalValues,
    getConclusionFieldTypeDrawerBehavior,
    getSampleConclusionFieldSchema,
    useFieldButtons,
} from "./fieldBuilderUtils";
import {
    ConclusionField,
    ConclusionFieldType,
    ConclusionFieldTypeName,
    ConclusionFieldArray,
} from "./schema";
import { useRHFAdapterAtom } from "./useRHFAdapterAtom";

const fieldTypeNames: CommonFieldTypeName[] = [
    "TextInput",
    "TextArea",
    "NumericInput",
    "CheckboxGroup",
    "RadioButtonGroup",
    "Dropdown",
    "FileUpload",
    "Heading",
];

interface SampleConclusionFieldBuilderProps {
    blobs: SavedBlob[];
    disabled?: boolean;
    control: Control<TestingProgramFormSchema>;
}
export function SampleConclusionFieldBuilder({
    disabled,
    blobs,
    control,
}: SampleConclusionFieldBuilderProps) {
    const { t } = useTranslation();
    const blobsFormAtom = useBlobsFormAtom(blobs);

    const { field } = useController({
        control,
        name: "sampleConclusionTasks",
        disabled,
    });
    const schema = useMemo(() => getSampleConclusionFieldSchema(t), [t]);
    const fieldsFormAtom = useRHFAdapterAtom<ConclusionFieldArray>(
        field.value,
        field.onChange,
        schema
    );
    return (
        <FieldBuilder<
            ConclusionField,
            ConclusionFieldType,
            ConclusionFieldTypeName
        >
            contentLabel={t("TestingControlView_ConclusionTasks_Field_Title")}
            buttonPanelLabel={t("TestingControlView_ConclusionTasks_Elements")}
            noElementsLabel={t("TestingControlView_ConclusionTasks_NoElements")}
            fieldButtons={useFieldButtons(fieldTypeNames)}
            fieldsAtom={fieldsFormAtom}
            renderField={(fa, openDrawer) => (
                <ConclusionFieldCmp
                    fieldsAtom={fieldsFormAtom}
                    fieldAtom={fa}
                    disabled={disabled}
                    openDrawer={openDrawer}
                />
            )}
            renderFieldPlaceholder={(_fa) => <FieldPlaceholderCmp />}
            renderFieldDrawerHeader={(fa) => (
                <ConclusionFieldDrawerHeader fieldAtom={fa} />
            )}
            renderFieldDrawerCustomSection={(fa, ref) => (
                <ConclusionFieldCustomSection
                    ref={ref}
                    fieldAtom={fa}
                    disabled={disabled}
                />
            )}
            getFieldTypeConditionalValues={
                getConclusionFieldTypeConditionalValues
            }
            getFieldTypeDrawerBehavior={getConclusionFieldTypeDrawerBehavior}
            disabled={disabled}
            createNewField={(fieldTypeName, fieldType) =>
                createNewSampleConclusionField(fieldTypeName, t, fieldType)
            }
            blobsAtom={blobsFormAtom}
        />
    );
}
